import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Heart, Mail, Lock, User, Eye, EyeOff, ArrowLeft, Shield, CheckCircle, Loader, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AuthPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailSent, setShowEmailSent] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [showVerificationNeeded, setShowVerificationNeeded] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: '',
    agreeToTerms: false,
    agreeToHipaa: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [authError, setAuthError] = useState<string>('');

  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, resetPassword, resendVerification, isLoading, error } = useAuth();

  useEffect(() => {
    // Check if we're coming from a reset password link
    const hash = location.hash;
    if (hash && hash.includes('type=recovery')) {
      // Handle password reset flow
      setIsLogin(true);
      // Could extract the access token here if needed
    }
  }, [location]);

  // Clear auth error when switching between login/register
  useEffect(() => {
    setAuthError('');
    setErrors({});
  }, [isLogin]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!isLogin && !formData.firstName) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!isLogin && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!isLogin && !formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the Terms of Service';
    }

    if (!isLogin && !formData.agreeToHipaa) {
      newErrors.agreeToHipaa = 'You must acknowledge the HIPAA notice';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getAuthErrorMessage = (error: string) => {
    if (error.includes('Invalid login credentials') || error.includes('invalid_credentials')) {
      return 'No account found with these credentials. Please check your email and password, or create a new account.';
    }
    if (error.includes('Email not verified')) {
      return 'Please verify your email address before signing in. Check your inbox for a verification link.';
    }
    if (error.includes('User already registered')) {
      return 'An account with this email already exists. Please sign in instead or use a different email address.';
    }
    if (error.includes('Password should be at least 6 characters')) {
      return 'Password must be at least 6 characters long.';
    }
    if (error.includes('Unable to validate email address')) {
      return 'Please enter a valid email address.';
    }
    if (error.includes('Signup not allowed')) {
      return 'New account registration is currently disabled. Please contact support.';
    }
    if (error.includes('Email rate limit exceeded')) {
      return 'Too many email requests. Please wait a few minutes before trying again.';
    }
    return error; // Return original error if no specific match
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log("Handling auth form submission, isLogin:", isLogin);
    if (!validateForm()) return;

    // Clear previous auth errors
    setAuthError('');

    try {
      if (isLogin) {
        console.log("Attempting login with email:", formData.email);
        await login(formData.email, formData.password);
        console.log("Login successful, navigating to dashboard");
        navigate('/dashboard');
      } else {
        console.log("Attempting registration with email:", formData.email);
        await register(formData.email, formData.password, formData.firstName, formData.lastName || '');
        setShowEmailSent(true);
      }
    } catch (error: any) {
      console.error('Authentication failed:', error);
      
      const errorMessage = error.message || 'An unexpected error occurred';
      
      // Handle specific error cases
      if (errorMessage.includes('Email not verified')) {
        setShowVerificationNeeded(true);
      } else {
        // Set user-friendly error message
        setAuthError(getAuthErrorMessage(errorMessage));
      }
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) {
      setErrors({ email: 'Email is required' });
      return;
    }

    setAuthError('');

    try {
      await resetPassword(formData.email);
      setShowEmailSent(true);
      setShowResetForm(false);
    } catch (error: any) {
      console.error('Failed to send reset email:', error);
      setAuthError(getAuthErrorMessage(error.message || 'Failed to send reset email'));
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setErrors({ email: 'Email is required' });
      return;
    }

    setAuthError('');

    try {
      await resendVerification(formData.email);
      setShowEmailSent(true);
      setShowVerificationNeeded(false);
    } catch (error: any) {
      console.error('Failed to resend verification email:', error);
      setAuthError(getAuthErrorMessage(error.message || 'Failed to resend verification email'));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear auth error when user starts typing in email or password fields
    if ((name === 'email' || name === 'password') && authError) {
      setAuthError('');
    }
  };

  if (showEmailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-orange-50 via-stone-50 to-teal-50">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Check Your Email</h2>
          <p className="text-slate-600 mb-6">
            We've sent you an email with {isLogin ? 'password reset instructions' : 'a verification link'}. 
            Please check your inbox and follow the instructions to continue.
          </p>
          <button
            onClick={() => {
              setShowEmailSent(false);
              setShowResetForm(false);
              setIsLogin(true);
              setAuthError('');
            }}
            className="text-teal-600 hover:text-teal-700 font-medium"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  if (showVerificationNeeded) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-orange-50 via-stone-50 to-teal-50">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-8 h-8 text-orange-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Email Verification Required</h2>
          <p className="text-slate-600 mb-6">
            Your email address needs to be verified before you can log in. Please check your inbox for a verification link.
          </p>
          
          {authError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-600">{authError}</p>
            </div>
          )}
          
          <div className="space-y-4">
            <button
              onClick={handleResendVerification}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white py-3 rounded-xl font-semibold hover:from-teal-600 hover:to-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Resend Verification Email'
              )}
            </button>
            <button
              onClick={() => {
                setShowVerificationNeeded(false);
                setIsLogin(true);
                setAuthError('');
              }}
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showResetForm) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-orange-50 via-stone-50 to-teal-50">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
          <button
            onClick={() => {
              setShowResetForm(false);
              setAuthError('');
            }}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </button>
          
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-500 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Reset Password</h2>
            <p className="text-slate-600">Enter your email to receive reset instructions</p>
          </div>

          <form onSubmit={handleResetPassword} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
                    errors.email ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            {authError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-600">{authError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white py-3 rounded-xl font-semibold hover:from-teal-600 hover:to-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Email'
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-orange-50 via-stone-50 to-teal-50">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-500 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            {isLogin ? 'Welcome Back' : 'Join Mira'}
          </h2>
          <p className="text-slate-600">
            {isLogin ? 'Continue your wellness journey' : 'Start your personalized mental wellness journey'}
          </p>
        </div>

        <div className="relative flex items-center justify-center mb-6">
          <div className="flex-grow border-t border-slate-200"></div>
          <span className="flex-shrink mx-4 text-slate-400">Sign in to continue</span>
          <div className="flex-grow border-t border-slate-200"></div>
        </div>

        {(error || authError) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-600">{authError || getAuthErrorMessage(error || '')}</p>
              {isLogin && (authError?.includes('No account') || authError?.includes('incorrect') || error?.includes('invalid_credentials')) && (
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-red-500">
                    Don't have an account? <button 
                      onClick={() => setIsLogin(false)} 
                      className="underline hover:text-red-700 font-medium"
                    >
                      Sign up here
                    </button>
                  </p>
                  <p className="text-xs text-red-500">
                    Forgot your password? <button 
                      onClick={() => setShowResetForm(true)} 
                      className="underline hover:text-red-700 font-medium"
                    >
                      Reset it here
                    </button>
                  </p>
                </div>
              )}
              {!isLogin && (authError?.includes('already exists') || error?.includes('already registered')) && (
                <p className="text-xs text-red-500 mt-1">
                  Already have an account? <button 
                    onClick={() => setIsLogin(true)} 
                    className="underline hover:text-red-700 font-medium"
                  >
                    Sign in here
                  </button>
                </p>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {!isLogin && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 mb-2">
                  First Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
                      errors.firstName ? 'border-red-300' : 'border-slate-300'
                    }`}
                    placeholder="First name"
                  />
                </div>
                {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
                    errors.lastName ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="Last name"
                />
                {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
              </div>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
                  errors.email ? 'border-red-300' : 'border-slate-300'
                }`}
                placeholder="Enter your email"
              />
            </div>
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
                  errors.password ? 'border-red-300' : 'border-slate-300'
                }`}
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
          </div>

          {!isLogin && (
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
                    errors.confirmPassword ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="Confirm your password"
                />
              </div>
              {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
            </div>
          )}

          {!isLogin && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                  className={`mt-1 w-4 h-4 text-teal-600 border rounded focus:ring-teal-500 ${
                    errors.agreeToTerms ? 'border-red-300' : 'border-slate-300'
                  }`}
                />
                <label htmlFor="agreeToTerms" className="text-sm text-slate-700">
                  I agree to the{' '}
                  <a href="#" className="text-teal-600 hover:text-teal-700 underline">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="#" className="text-teal-600 hover:text-teal-700 underline">
                    Privacy Policy
                  </a>
                </label>
              </div>
              {errors.agreeToTerms && <p className="text-sm text-red-600">{errors.agreeToTerms}</p>}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900 mb-1">HIPAA Compliance Notice</h4>
                    <p className="text-sm text-blue-800 mb-3">
                      Your health information is protected under HIPAA. We use industry-standard encryption 
                      and security measures to protect your personal and health information.
                    </p>
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="agreeToHipaa"
                        name="agreeToHipaa"
                        checked={formData.agreeToHipaa}
                        onChange={handleInputChange}
                        className="mt-0.5 w-4 h-4 text-teal-600 border-slate-300 rounded focus:ring-teal-500"
                      />
                      <label htmlFor="agreeToHipaa" className="text-sm text-blue-900">
                        I acknowledge that I have read and understand the HIPAA privacy practices
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              {errors.agreeToHipaa && <p className="text-sm text-red-600">{errors.agreeToHipaa}</p>}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white py-3 rounded-xl font-semibold hover:from-teal-600 hover:to-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Please wait...
              </>
            ) : (
              isLogin ? 'Sign In' : 'Create Account'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          {isLogin ? (
            <div className="space-y-3">
              <button
                onClick={() => setShowResetForm(true)}
                className="text-teal-600 hover:text-teal-700 text-sm font-medium"
              >
                Forgot your password?
              </button>
              <p className="text-slate-600 text-sm">
                Don't have an account?{' '}
                <button
                  onClick={() => setIsLogin(false)}
                  className="text-teal-600 hover:text-teal-700 font-medium"
                >
                  Sign up
                </button>
              </p>
            </div>
          ) : (
            <p className="text-slate-600 text-sm">
              Already have an account?{' '}
              <button
                onClick={() => setIsLogin(true)}
                className="text-teal-600 hover:text-teal-700 font-medium"
              >
                Sign in
              </button>
            </p>
          )}
        </div>

        <div className="mt-8 pt-6 border-t border-slate-200 text-center">
          <p className="text-xs text-slate-500">
            By using Mira, you acknowledge that this service is not a replacement for professional 
            mental health care. If you're experiencing a crisis, please contact emergency services 
            or call 1 246 5364500 for the Suicide & Crisis Lifeline.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;